import { Card } from "reactstrap";
import { Whisper, Popover } from "rsuite";
import { CFieldType } from "../../../../types/RecordInterfaces";
import EditableText from "../../../../elements/EditableText";
import { Dispatch, SetStateAction, useState } from "react";
import SelectableOption from "../../../SelectableInput/SelectableOption";
import Options from "./Options";
import { FieldOptionsType } from "../ComplexField";
import useHover from "../../../../hooks/useHover";

export enum ComplexType {
  "Phone" = "tel",
  "Email" = "email",
  "URL" = "text",
}

const ColorMap: any = {
  Email: {
    Personal: "#8DC6DF",
    Work: "#F1E696",
    Other: "#B4B3AE",
  },
  Phone: {
    Mobile: "#9AD998",
    Business: "#F1E696",
    Personal: "#8DC6DF",
    Other: "#B4B3AE",
  },
  URL: {
    Website: "#B4E2E5",
    Support: "#B4B3AE",
    LinkedIn: "#88C8E0",
    Facebook: "#4267B2",
    Instagram: "#E1306C",
    TikTok: "#ff0050",
    Blog: "#EDDBA8",
    Other: "#B4B3AE",
  },
};

interface Props {
  complexVal: CFieldType;
  onChange: Dispatch<SetStateAction<CFieldType[]>>;
  setIsEditing: (value: boolean) => void;
  setEditorOpen: Dispatch<SetStateAction<boolean>>;
  setError: (error: string) => void;
  onOpen: () => void;
  FieldOptions: FieldOptionsType;
  fieldType: "Email" | "Phone" | "URL";
}

const ComplexOption = ({
  complexVal,
  onChange,
  setIsEditing,
  setEditorOpen,
  setError,
  onOpen,
  FieldOptions,
  fieldType,
}: Props) => {
  const [existingValue, setValue] = useState(complexVal);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [itemRef, isHovered] = useHover<HTMLDivElement>(popoverOpen);
  const [editingText, setEditingText] = useState(false);

  return (
    <div
      className="d-flex justify-content-between align-items-center mb-1"
      style={{ height: 27 }}
      ref={itemRef}
    >
      <div className="d-flex align-items-center">
        <Whisper
          trigger="click"
          placement="bottomStart"
          onOpen={() => setIsEditing(true)}
          onClose={() => setIsEditing(false)}
          speaker={
            <Popover style={{ width: 200 }}>
              <Card className="pt-2 pb-1 m-0">
                {FieldOptions.map((opt) => (
                  <div key={opt.value} className="m-1">
                    {/* @ts-ignore */}
                    <SelectableOption
                      onSelect={(val: any) => {
                        setIsEditing(true);

                        setValue((prev) => {
                          const updatedValue = { ...prev, Type: val };

                          editValue(updatedValue.id, updatedValue);

                          return updatedValue;
                        });
                      }}
                      onDeselect={() => {
                        setValue((prev) => ({ ...prev, Type: undefined }));
                      }}
                      value={complexVal.Type}
                      option={opt}
                      labelKey="label"
                      valueKey="value"
                      colorKey="color"
                    />
                  </div>
                ))}
              </Card>
            </Popover>
          }
        >
          <i
            className={`${
              complexVal?.Type?.color
                ? "mdi mdi-checkbox-blank-circle"
                : "mdi mdi-checkbox-blank-circle-outline"
            } mr-1 cursor-pointer`}
            style={{
              fontSize: "20px",
              color:
                ColorMap?.[fieldType]?.[complexVal?.Type?.value || "1"] ||
                complexVal?.Type?.color ||
                "var(--light)",
              lineHeight: 1,
            }}
          />
        </Whisper>
        <EditableText
          syncEditState={setEditingText}
          savedText={complexVal[fieldType] as string}
          text={existingValue[fieldType] as string}
          onChange={(newEmail) =>
            setValue((prev) => ({ ...prev, [fieldType]: newEmail }))
          }
          onSave={(newVal) =>
            editValue(complexVal.id, { ...complexVal, [fieldType]: newVal })
          }
          editStyles={{
            width: 300,
            fontSize: 13,
            position: "relative",
            top: 0,
            left: -2,
          }}
          displayStyles={{ fontSize: 13, maxWidth: isHovered ? 205 : 300 }}
          onOpen={() => {
            onOpen();
            setValue(complexVal);
            setEditorOpen(false);
          }}
          setError={() => setError(`Invalid ${fieldType}`)}
          type={fieldType}
        />
      </div>

      {isHovered && !editingText && (
        <Options
          fieldType={fieldType}
          itemId={complexVal.id}
          setIsEditing={(bool: boolean) => {
            setIsEditing(bool);
            setPopoverOpen(bool);
          }}
          complexVal={complexVal}
          existingValue={existingValue}
          setOptionValue={setValue}
          onChange={onChange}
        />
      )}
    </div>
  );

  function editValue(id: string, newVal: CFieldType) {
    onChange((prev: Array<CFieldType>) => {
      return prev.map((email) => {
        if (email.id === id) return newVal;
        return email;
      });
    });
  }
};

export default ComplexOption;
